/** Known feature-flag names. Every flag must appear here. */
export const FLAG_NAMES = [
  'MIGRATION',
  'MIGRATION_MOCK_DATA',
  'MIGRATION_DATA_REVIEW',
  'PROFILE_API_DISPLAY_STRINGS',
] as const;

export type FlagName = (typeof FLAG_NAMES)[number];

/** localStorage key prefix used for all flag overrides. */
const FF_PREFIX = 'cssd.ff.';

/**
 * Flags that cannot be enabled via a localStorage override when the app is
 * running as a production build. The build default is always used instead.
 */
const PROD_BLOCKED_FLAGS = new Set<FlagName>(['MIGRATION_MOCK_DATA', 'MIGRATION_DATA_REVIEW']);

// ---------------------------------------------------------------------------
// Pure resolver — the primary testable seam.
// ---------------------------------------------------------------------------

/**
 * Resolves the full flags snapshot from an env source, a storage source, and
 * a production-mode signal.
 *
 * Rules (in priority order):
 * 1. If `isProd` is true and the flag is in `PROD_BLOCKED_FLAGS`, the env
 *    default wins unconditionally (override ignored).
 * 2. If a valid override (`'true'` or `'false'`) is stored under
 *    `cssd.ff.<FLAG>`, it decides the value.
 * 3. Otherwise, the env default (`VITE_FF_<FLAG> === 'true'`) is used.
 *
 * Only the exact strings `'true'` and `'false'` are honoured as overrides.
 * Any other stored value (null, `''`, `'yes'`, …) falls back to the env
 * default, so a typo can never silently misconfigure the app.
 *
 * Exported so tests can call it directly with injected plain objects.
 * Consumers should read the eager `featureFlags` export instead.
 */
export function resolveFeatureFlags(
  env: Record<string, string | undefined>,
  storage: Pick<Storage, 'getItem'>,
  isProd: boolean
): Record<FlagName, boolean> {
  const result = {} as Record<FlagName, boolean>;

  for (const flag of FLAG_NAMES) {
    const envDefault = env[`VITE_FF_${flag}`] === 'true';

    if (isProd && PROD_BLOCKED_FLAGS.has(flag)) {
      // Production blocklist: ignore any stored override.
      result[flag] = envDefault;
      continue;
    }

    const stored = storage.getItem(`${FF_PREFIX}${flag}`);

    if (stored === 'true') {
      result[flag] = true;
    } else if (stored === 'false') {
      result[flag] = false;
    } else {
      result[flag] = envDefault;
    }
  }

  return result;
}

// ---------------------------------------------------------------------------
// console helper factory — thin adapter, injectable for tests.
// ---------------------------------------------------------------------------

type CssdHelper = {
  setFlag(name: string, value: boolean): void;
  clearFlag(name: string): void;
  reset(): void;
  reload(): void;
  readonly flags: Record<FlagName, boolean>;
};

/**
 * Creates the `window.cssd` console helper.
 *
 * `setFlag`/`clearFlag`/`reset` write only to the injected `storage`; they
 * never touch anything outside the `cssd.ff.` key namespace. `reload` always
 * delegates to `window.location.reload` (not injectable — it is a deliberate
 * browser-side action, not business logic).
 *
 * Exported so tests can call it with an injected storage object.
 */
export function createCssdHelper(flags: Record<FlagName, boolean>, storage: Storage): CssdHelper {
  return {
    setFlag(name: string, value: boolean): void {
      if (!(FLAG_NAMES as readonly string[]).includes(name)) {
        console.error(`[cssd] Unknown flag "${name}". Known flags: ${FLAG_NAMES.join(', ')}`);
        return;
      }
      storage.setItem(`${FF_PREFIX}${name}`, value ? 'true' : 'false');
      console.log(`[cssd] Flag "${name}" set to ${String(value)}. Reload to apply.`);
    },

    clearFlag(name: string): void {
      storage.removeItem(`${FF_PREFIX}${name}`);
      console.log(`[cssd] Override for "${name}" cleared. Reload to apply.`);
    },

    reset(): void {
      const toRemove: string[] = [];
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key?.startsWith(FF_PREFIX)) {
          toRemove.push(key);
        }
      }
      toRemove.forEach((k) => storage.removeItem(k));
      console.log(
        `[cssd] All flag overrides cleared (${toRemove.length} key(s)). Reload to apply.`
      );
    },

    reload(): void {
      window.location.reload();
    },

    get flags(): Record<FlagName, boolean> {
      return flags;
    },
  };
}

// ---------------------------------------------------------------------------
// Eager snapshot — computed once at module load.
// ---------------------------------------------------------------------------

/**
 * Resolved feature flags for the current session.
 *
 * Values are set at module load from build-time `VITE_FF_*` env vars,
 * overlaid with any valid `localStorage` overrides written via `window.cssd`.
 * An override takes effect on the *next page load* (not live), because these
 * values are read by non-reactive module-level code.
 *
 * Consumers: read `featureFlags.MIGRATION` etc. as plain property access.
 * Do not mutate this object.
 */
const featureFlags = resolveFeatureFlags(
  import.meta.env as unknown as Record<string, string | undefined>,
  window.localStorage,
  import.meta.env.MODE === 'production'
);

// Expose the console helper on window in every environment (including
// production — that is the point of this feature).
window.cssd = createCssdHelper(featureFlags, window.localStorage);

export default featureFlags;
